import { Container, Graphics, Rectangle, RenderTexture, Sprite, Text, Texture } from "pixi.js"
import { EmberMemberStatus, EmberRosterEntry, EmberRosterRole, Entity, EntityId } from "game-common/models"
import { locateInventoryActiveItem } from "game-common/item/item"
import { GuiManager } from "../gui_manager"
import { LpcLayered } from "../sprites/lpc_layered"
import { FlameBackdrop } from "./flame_backdrop"
import { TextRenderer } from "../text/text_renderer"
// Reached for only to photograph a dying card (see captureCard); imported the same way
// effects/sprite_shatter.ts reaches for it, and read lazily so the cycle never bites at load.
import { PixiJsClientRenderer } from "../pixijs_client_renderer"
import { disposeTextTextures } from "../text/dispose_text"
import { BubbleTarget, MAX_BUBBLE_H, RosterSpeechBubble } from "./roster_speech"
import { RosterSpeechLane } from "./roster_speech_lane"
// The card's hand-placed geometry and colour ramps (pure, and tested on their own).
import {
    BADGE_SIZE,
    BADGE_X,
    BADGE_Y,
    DAMAGE_GHOST,
    DAMAGE_NUMBER_COLOR,
    DAMAGE_NUMBER_Y,
    DAMAGE_RING,
    DAMAGE_WASH,
    HP_H,
    HP_MARGIN,
    HP_Y,
    HUNGER_H,
    HUNGER_PULSE_MS,
    HUNGER_URGENT,
    HUNGER_Y,
    hpColor,
    hungerColor,
    INNER_MARGIN,
    INNER_RADIUS,
    NAME_MAX_CHARS,
    NAME_Y,
    PANEL_PAD,
    PATIENCE_INSET,
    PATIENCE_URGENT,
    PATIENCE_W,
    patienceColor,
    PORTRAIT_SCALE,
    PORTRAIT_X,
    PORTRAIT_Y,
    ROLE_EMOJI,
    SCRIM_H,
    SLOT_H,
    SLOT_W,
} from "./ember_roster_card"
import { drawFlareGlow, FLARE_MS } from "./flare"
// The status pill row above the strip -- what each member is doing -- and the pill shape the lord's
// fire warning shares with it.
import { drawPillPlate, fitPillText, RosterStatusRow, STATUS_PILL_GAP, STATUS_PILL_W } from "./roster_status"
import { DamageFrame, RosterDamageFx } from "./roster_damage"
import {
    DEATH_MS,
    FragmentSeed,
    GREY_MS,
    captureExtent,
    fragmentAt,
    greyAt,
    isFinished,
    isShattering,
    seedFragments,
} from "./roster_death"

const ROLE_COLORS: Record<EmberRosterRole, number> = {
    lord: 0xffd23f,
    // The same cool green a partner's dot gets on the minimap, so the card and the dot are
    // recognisably the same person without having to read either.
    player: 0x2bc4a8,
    defender: 0xff8a5c,
    forager: 0x7be08a,
    // Ember gold: the colour of the fire that drew them in, and deliberately not in the camp's own
    // palette -- a visitor is warm, welcome and not yet one of these people.
    visitor: 0xffb648,
}

// Must agree with ROLE_ORDER in the server's ember_roster campaign, which is what actually sorts
// the strip; this one re-sorts locally after a card is added or removed.
const ROLE_ORDER: Record<EmberRosterRole, number> = {
    visitor: -1,
    lord: 0,
    player: 1,
    defender: 2,
    forager: 3,
}

// Uniform scale for the whole widget (portraits, text, emoji, spacing) — cheaper and more reliable
// than re-tuning every geometry constant below.
const WIDGET_SCALE = 0.8

// Kept back from the edge of a texture a shard is cut out of. A frame that measures EXACTLY to the
// edge is a floating-point coin toss against pixi's "does this fit" test, and losing it throws; a
// hundredth of a pixel is not a thing anybody can see. See shatterCard.
const FRAME_EPSILON = 0.01

// ===== the visitor's detached slot =====
//
// A visitor is drawn on its OWN little plate, set apart from the household's, with two dots
// crawling across the gap between them. The gap is the whole statement: this person is at the fire
// and is not one of the camp, and the only thing standing between those two facts is the player.
//
// It opens on the LEFT because the strip is anchored by its right edge (positionEmberRoster), so
// everything to the right of the gap -- the lord and the crew the player has learned the position
// of -- stays exactly where it was while a stranger comes and goes.
const VISITOR_GAP = 26

// The two dots in the gap. They travel toward the HOUSEHOLD, which is what makes them read as
// "wants in" rather than "leaving"; running them the other way is this widget's departure.
const DOT_COUNT = 2
const DOT_R = 2.1
const DOT_TRAVEL_MS = 1500
// How much of the cycle separates one dot from the next.
const DOT_STAGGER = 0.42
// Once the bargain is struck the dots quicken -- the thing they are counting down to is close.
const DOT_FED_SPEEDUP = 2.1

// The join: the gap closes and the card slides from the visitor slot into the household. Long
// enough to be seen as a movement rather than a jump, short enough that it never delays the camp.
export const JOIN_MS = 480
// The departure: they walk back into the dark, so the card leaves the way they came -- further
// left, and out.
export const LEAVE_MS = 620
const LEAVE_DRIFT = 26

// Child z-order within a card (container.sortableChildren) so the fire backdrop sits behind the
// portrait but above the frame, regardless of add/rebuild order.
const Z_CHROME = 0
const Z_FLAME = 1
const Z_PORTRAIT = 2
const Z_SCRIM = 3
// The damage wash goes over the portrait but UNDER the name and stat bars, so a card can shout that
// it was hit without hiding the numbers the player is being told to look at.
const Z_WASH = 4
const Z_BADGE = 5
const Z_NAME = 6
const Z_HP = 7
const Z_HUNGER = 8
const Z_SELECTION = 9
const Z_SPEAKING = 10
const Z_PATIENCE = 11
// Over the card's own chrome: the flare bleeds OUTSIDE the frame (that is what makes it a glow
// rather than a border), so nothing on the card should be drawn on top of it.
const Z_FLARE = 14
const Z_DAMAGE_RING = 12
const Z_DAMAGE_NUMBER = 13
// The grey a dead member's card goes under sits over everything else on it.
const Z_DEATH_PALL = 15

// Ash: mid-grey, and just short of opaque (see drawPall).
const PALL_COLOR = 0x5a5f5a
const PALL_MAX_ALPHA = 0.88

// Offscreen speech lane: a camp member speaking while OUT of the player's view gets their line
// shown over their roster card. The rules (one bubble at a time, dwell, queue cap, card flash) are
// in roster_speech_lane.ts; this file only draws what that decides. The lane's height comes from
// the bubble itself so the two can't drift apart — it is reserved whether or not anyone is talking,
// so the follow-cam PiP sitting above the strip never jumps.
const FIRE_WARNING_H = 30
const FIRE_WARNING_PULSE_MS = 600
const SPEECH_LANE_H = MAX_BUBBLE_H + FIRE_WARNING_H

interface RosterSlot {
    container: Container
    canKick?: boolean
    portrait: LpcLayered
    mask: Graphics
    badge: Text
    nameText: Text
    hpBar: Graphics
    // Recruits only: how full they are (1 = just ate, 0 = about to walk out). Hidden on the lord's
    // card, which has no hunger arc.
    hungerBar: Graphics
    hunger?: number
    // Visitors only: the draining patience ring, and the two values that draw it. A member of the
    // camp carries neither -- `patience` stays undefined and the ring is never shown.
    patienceRing: Graphics
    patience?: number
    fed?: boolean
    // Visitors only: the "look here" flare the card arrives wearing, and how much of it is left.
    // The same shape every HUD widget that turns up UNASKED uses (gui/flare.ts) -- which is what
    // this is: nobody asked for a stranger, and the card appears in the corner of a screen whose
    // middle is usually a wave.
    flare?: Graphics
    flareMs?: number
    selection: Graphics
    appearanceKey: string
    role: EmberRosterRole
    name?: string
    // What this member is doing, for the pill above their card. The row itself owns the drawing (see
    // RosterStatusRow); the slot only carries the value so `layout` can hand it over with the card's
    // resting x.
    status?: EmberMemberStatus
    hp?: number
    hpMax?: number
    // Where the card sits at rest, so the damage shake can move it off that and back without the
    // next roster tick fighting the animation for `container.x`.
    baseX: number
    // Which column of the strip the card occupies. A card whose member has died goes on holding its
    // column while it breaks up, and the survivors lay out around the gap (see `layout`).
    column: number
    // Lord only: animated campfire-strength particle backdrop + its clip mask, and the current
    // strength (0..1) driving it.
    flame?: FlameBackdrop
    flameMask?: Graphics
    fireStrength: number
    // Role-coloured ring that flashes when this member speaks offscreen.
    speaking: Graphics
    speakingRole?: EmberRosterRole
    // Damage effect: the red wash over the portrait, the ring around the frame, and the floating
    // "-N" (built on the first hit the card takes, then reused). What they do is decided by
    // RosterDamageFx; the slot only holds the objects and what was last drawn.
    wash: Graphics
    damageRing: Graphics
    damageNumber?: Text
    damageSeq: number
    // Last ghost position drawn into the HP bar, so a card only repaints its bar when the pale
    // segment has actually moved (it spends a fifth of the effect held still).
    ghostRatio?: number
    // THE JOIN. A visitor that takes a job keeps its card and its entity id -- all that changes is
    // which side of the gap it belongs on. `joinMs` counts out the slide from the visitor slot into
    // the household, from `joinFromX`; while it runs, `layout()` leaves the card's x alone.
    joinMs?: number
    joinFromX?: number
}

// A visitor walking back out into the dark. NOT a DyingCard: nobody died, they gave up on the camp,
// so the card drifts further left and fades rather than greying over and shattering. It keeps no
// column -- the household never moved to make room for it, so there is nothing to close up.
interface LeavingCard {
    slot: RosterSlot
    elapsedMs: number
    fromX: number
}

// A card on its way out: its member has died, so the card greys over, breaks into shards and takes
// its column with it. The slot is kept whole until the very end (rather than torn down at the start)
// because the grey phase is the card ITSELF greying -- portrait, name, bars and all.
interface DyingCard {
    slot: RosterSlot
    column: number
    elapsedMs: number
    pall: Graphics
    // The ash the card greys under. An OVERLAY rather than a ColorMatrixFilter: a filter on the card
    // container renders it as nothing here (pixi has to derive a filter area from the card's bounds,
    // and the paperdoll portrait inside does not give it a usable one), and a filtered card also
    // costs a render target per frame on hardware this has to run on.
    // Built at the moment the card breaks: a snapshot of the grey card, sliced into shards.
    shards?: Container
    pieces?: Sprite[]
    seeds?: FragmentSeed[]
    texture?: RenderTexture
    shattered: boolean
}

// Clickable HUD panel of the ember camp's notable NPCs (lord / defenders / foragers). Fed by the
// per-room roster broadcast (Protocol.type_updateEmberRoster). Each card is a framed, zoomed-in
// portrait (the LpcLayered paperdoll from the inventory view) filling the card like a background,
// with an emoji role badge, a short name and an HP bar overlaid. Clicking opens the follow-cam PiP.
export class EmberRosterWidget extends Graphics {
    guiManager: GuiManager
    private slots: Map<EntityId, RosterSlot> = new Map()
    private selectedEntityId?: EntityId

    // Offscreen speech: the rules live in the lane, the bubble draws what it decides.
    private fireWarning = new Graphics()
    private fireWarningTitle: Text
    private fireWarningState?: "No fire" | "Fire low"
    private fireWarningPulseMs = 0
    // One pill per member, in the same lane as the fire warning above (see roster_status.ts).
    private statusRow = new RosterStatusRow()
    private speechBubble: RosterSpeechBubble
    private speechLane = new RosterSpeechLane()
    // Hits taken by camp members, read off the health each roster tick reports. The curves and
    // timings live in roster_damage.ts; this widget only draws the frame it is handed.
    private damageFx = new RosterDamageFx()
    // Cards whose member has died: greying, then breaking apart, and holding their column in the
    // strip until the last shard has gone (see roster_death.ts). Keyed by the entity that left, so a
    // member who somehow comes back gets a fresh card rather than the one that is falling apart.
    private dying: Map<EntityId, DyingCard> = new Map()
    // The living cards in display order, remembered so the strip can be laid out again the moment a
    // death finishes rather than waiting for the next roster tick to close the gap.
    private order: EntityId[] = []
    // Visitors who gave up and walked out, drifting off the left of the strip. Separate from
    // `dying` because they take no column with them and never break apart.
    private leaving: Map<EntityId, LeavingCard> = new Map()
    // Free-running clock for the starving-member pulse, shared by every card so they breathe together
    // rather than each on its own phase.
    private pulseMs = 0
    // How many columns at the left of the strip are visitors. 0 means no gap and no dots, which is
    // every camp with nobody at the fire -- i.e. the strip as it has always looked.
    private visitorColumns = 0
    // Whether the strip is split at all. Held open a little past the last visitor: while one is
    // walking out there is nobody on the left any more, but the space they left -- and the dots
    // running back across it -- is the departure.
    private gapOpen = false
    // The dots crawling across the gap. Their own Graphics because they are redrawn every frame and
    // the panel behind the cards is not.
    private joinDots: Graphics
    private dotMs = 0

    constructor(guiManager: GuiManager) {
        super()
        this.guiManager = guiManager
        this.scale.set(WIDGET_SCALE)
        this.joinDots = new Graphics()
        this.joinDots.visible = false
        this.addChild(this.joinDots)
        // Added before the bubble and the fire warning, which both sit in or above the same lane and
        // should draw over a chip rather than under one.
        this.addChild(this.statusRow)
        this.speechBubble = new RosterSpeechBubble()
        this.addChild(this.speechBubble)
        this.fireWarningTitle = new TextRenderer().role("caption").bold().render("") as Text
        this.fireWarning.addChild(this.fireWarningTitle)
        this.fireWarning.visible = false
        this.addChild(this.fireWarning)
        this.exposeDevTriggers()
    }

    // Where column `n` sits, in the widget's own space. Identical to the old `n * SLOT_W` until a
    // visitor turns up, at which point everything from the first household column rightwards is
    // pushed clear by the gap -- and because the widget is right-anchored, "pushed clear" means the
    // VISITOR hangs off the left while the household stays put.
    private columnX = (column: number): number =>
        column * SLOT_W + (this.gapOpen && column >= this.visitorColumns ? VISITOR_GAP : 0)

    // How many cards are mid-hire, crossing from the visitor slot into the household.
    private joining = (): number => {
        let count = 0
        this.slots.forEach(slot => {
            if (slot.joinMs !== undefined) {
                count++
            }
        })
        return count
    }

    // Browser-console triggers, in the same shape as window.playerRing / window.spriteShadow:
    //
    //     rosterHit()        // every card takes a middling hit
    //     rosterHit(0.4)     // ...takes 40% of its health off
    //     rosterKill()       // the last card on the strip dies and breaks apart
    //     rosterWidget       // the strip itself: cardCount(), isDying(id), deathPhase(id)
    //
    // Neither reaction can be looked at on demand -- one needs something off-screen to land a blow,
    // the other needs somebody to die -- so there would otherwise be no way to tune the shake, the
    // colours or the shatter. The health rosterHit pretends was lost is corrected by the next roster
    // tick half a second later; the card rosterKill takes is rebuilt by it.
    private exposeDevTriggers = () => {
        if (typeof window === "undefined") {
            return
        }
        ;(window as any).rosterWidget = this
        ;(window as any).rosterKill = (name?: string): string => {
            const entry = Array.from(this.slots.entries())
                .reverse()
                .find(([, slot]) => !name || (slot.name || "").toLowerCase().includes(name.toLowerCase()))
            if (!entry) {
                return `[rosterKill] no card matching ${name ?? "(any)"}`
            }
            this.beginDeath(entry[0], entry[1])
            this.layout()
            this.guiManager.positionEmberRoster()
            return entry[0]
        }
        ;(window as any).rosterHit = (fraction: number = 0.18): string => {
            this.slots.forEach((slot, entityId) => {
                const hpMax = slot.hpMax ?? 20
                const hp = slot.hp ?? hpMax
                const hit = Math.max(0, hp - hpMax * fraction)
                this.damageFx.hit(entityId, hp, hit, hpMax)
                slot.hp = hit
                this.drawHpBar(slot.hpBar, hit, hpMax, slot.ghostRatio)
            })
            return `[rosterHit] ${this.slots.size} card(s)`
        }
    }

    private appearanceKeyFor = (entry: EmberRosterEntry): string =>
        entry.appearance ? JSON.stringify(entry.appearance) : ""

    private truncate = (name?: string): string => {
        if (!name) {
            return ""
        }
        return name.length > NAME_MAX_CHARS ? name.slice(0, NAME_MAX_CHARS - 1) + "…" : name
    }

    // Build the zoomed front-facing portrait from a synthetic entity, mirroring the inventory-view
    // portrait (dynamic_dialog.ts / EntityDisplayer). Clipped to the card via `mask` by the caller.
    private buildPortrait = (entry: EmberRosterEntry): LpcLayered => {
        const entity = new Entity()
        entity.weapon = { weaponType: locateInventoryActiveItem(entry.inventory, "weapon")?.type as any }
        entity.appearance = entry.appearance
        entity.inventory = entry.inventory

        const portrait = new LpcLayered(entry.entityId)
        try {
            portrait.updatePaperdoll(entity)
        } catch (e) {
            // Malformed appearance data must not take down the whole roster — leave it blank.
            console.warn(`[EmberRoster] portrait build failed for ${entry.entityId}`, e)
        }
        portrait.scale.set(PORTRAIT_SCALE)
        portrait.x = PORTRAIT_X
        portrait.y = PORTRAIT_Y
        return portrait
    }

    private makeMask = (): Graphics => {
        const g = new Graphics()
        g.beginFill(0xffffff)
        g.drawRoundedRect(
            INNER_MARGIN,
            INNER_MARGIN,
            SLOT_W - INNER_MARGIN * 2,
            SLOT_H - INNER_MARGIN * 2,
            INNER_RADIUS,
        )
        g.endFill()
        return g
    }

    private makeBadge = (role: EmberRosterRole): Text => {
        // No tint: keep the emoji's own colors.
        const t = new TextRenderer().role("label").render(ROLE_EMOJI[role]) as Text
        t.x = BADGE_X
        t.y = BADGE_Y
        return t
    }

    private makeNameText = (name?: string): Text => {
        const t = new TextRenderer().role("entityLabel").render(this.truncate(name)) as Text
        t.tint = 0xffffff
        t.x = Math.round(SLOT_W / 2 - t.width / 2)
        t.y = NAME_Y
        return t
    }

    // `ghostRatio` is the fighting-game "health you just lost": a pale segment left standing where
    // the bar was before the hit, drawn UNDER the live fill so only the difference shows, and drained
    // away by the damage effect a beat later. Undefined (the resting case) draws the bar as before.
    private drawHpBar = (bar: Graphics, hp?: number, hpMax?: number, ghostRatio?: number) => {
        bar.clear()
        const w = SLOT_W - HP_MARGIN * 2
        bar.beginFill(0x000000, 0.6)
        bar.drawRoundedRect(0, 0, w, HP_H, 2)
        bar.endFill()
        if (ghostRatio !== undefined && ghostRatio > 0) {
            bar.beginFill(DAMAGE_GHOST, 0.9)
            bar.drawRoundedRect(0, 0, Math.max(1, w * Math.min(1, ghostRatio)), HP_H, 2)
            bar.endFill()
        }
        if (hp !== undefined && hpMax && hpMax > 0) {
            const ratio = Math.max(0, Math.min(1, hp / hpMax))
            const color = hpColor(ratio)
            if (ratio > 0) {
                bar.beginFill(color, 0.95)
                bar.drawRoundedRect(0, 0, Math.max(1, w * ratio), HP_H, 2)
                bar.endFill()
            }
        }
        bar.x = HP_MARGIN
        bar.y = HP_Y
    }

    // The hunger bar: the recruit's whole food arc as one falling bar, drawn a shade thinner than the
    // HP bar above it and in warm food colours (amber -> orange -> ember red) so the two rows can't be
    // read as the same stat. The server sends no hunger for the lord, whose row stays empty.
    private drawHungerBar = (bar: Graphics, hunger?: number) => {
        bar.clear()
        bar.visible = hunger !== undefined
        if (hunger === undefined) {
            return
        }
        const w = SLOT_W - HP_MARGIN * 2
        bar.beginFill(0x000000, 0.6)
        bar.drawRoundedRect(0, 0, w, HUNGER_H, 2)
        bar.endFill()
        const ratio = Math.max(0, Math.min(1, hunger))
        const color = hungerColor(ratio)
        if (ratio > 0) {
            bar.beginFill(color, 0.95)
            bar.drawRoundedRect(0, 0, Math.max(1, w * ratio), HUNGER_H, 2)
            bar.endFill()
        }
        bar.x = HP_MARGIN
        bar.y = HUNGER_Y
    }

    // A VISITOR's patience, as a rim that unwinds. Drawn as an arc rather than a bar because a bar
    // on this card would be read as the hunger bar one row above it, and this is not hunger -- it
    // is the stranger's willingness to keep standing there. Starts at twelve o'clock and drains
    // clockwise, so the gap in the rim opens at the top where the eye already is.
    //
    // Nothing is drawn at all for a member of the camp: `patience` is undefined for everyone but a
    // visitor, which is also what keeps this off the lord's card.
    private drawPatienceRing = (ring: Graphics, patience?: number) => {
        ring.clear()
        ring.visible = patience !== undefined
        if (patience === undefined) {
            return
        }
        const ratio = Math.max(0, Math.min(1, patience))
        const x = PATIENCE_INSET
        const y = PATIENCE_INSET
        const w = SLOT_W - PATIENCE_INSET * 2
        const h = SLOT_H - PATIENCE_INSET * 2
        const radius = INNER_RADIUS + 1

        // The groove the ring drains out of, so the missing part still reads as "there was more
        // here" rather than as nothing.
        ring.lineStyle(PATIENCE_W, 0x000000, 0.42)
        ring.drawRoundedRect(x, y, w, h, radius)

        if (ratio <= 0) {
            return
        }
        // A rounded rect has no arc API, so the live part is walked as a polyline around the frame
        // from the top centre, clockwise, and cut off at `ratio` of the way round. The corners are
        // squared off in the path -- at a 2px stroke on a 6px radius that is a difference nobody
        // can see, and it keeps this to one cheap pass per redraw on hardware that counts them.
        const right = x + w
        const bottom = y + h
        const midX = x + w / 2
        const legs: Array<[number, number, number, number]> = [
            [midX, y, right, y],
            [right, y, right, bottom],
            [right, bottom, x, bottom],
            [x, bottom, x, y],
            [x, y, midX, y],
        ]
        const lengths = legs.map(([x1, y1, x2, y2]) => Math.abs(x2 - x1) + Math.abs(y2 - y1))
        const perimeter = lengths.reduce((a, b) => a + b, 0)
        let remaining = perimeter * ratio

        ring.lineStyle(PATIENCE_W, patienceColor(ratio), 0.95)
        ring.moveTo(midX, y)
        for (let i = 0; i < legs.length && remaining > 0; i++) {
            const [x1, y1, x2, y2] = legs[i]
            const length = lengths[i]
            if (length <= 0) {
                continue
            }
            const t = Math.min(1, remaining / length)
            ring.lineTo(x1 + (x2 - x1) * t, y1 + (y2 - y1) * t)
            remaining -= length
        }
    }

    // The card frame (dark, so the inset portrait reads as a bordered background) plus a bottom
    // scrim and a small chip behind the badge, all in one Graphics behind the portrait/overlays.
    private drawChrome = (chrome: Graphics) => {
        chrome.clear()
        // Frame / margin.
        chrome.beginFill(0x0a0d08, 0.9)
        chrome.lineStyle(1, 0xffffff, 0.1)
        chrome.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
        chrome.endFill()
    }

    private drawScrim = (scrim: Graphics) => {
        scrim.clear()
        scrim.beginFill(0x000000, 0.5)
        scrim.drawRoundedRect(
            INNER_MARGIN,
            SLOT_H - INNER_MARGIN - SCRIM_H,
            SLOT_W - INNER_MARGIN * 2,
            SCRIM_H,
            INNER_RADIUS,
        )
        scrim.endFill()
        // Chip behind the emoji badge (top-left) for contrast over any portrait.
        scrim.beginFill(0x000000, 0.45)
        scrim.drawRoundedRect(BADGE_X - 2, BADGE_Y - 1, BADGE_SIZE + 5, BADGE_SIZE + 3, 3)
        scrim.endFill()
    }

    // The flash a card gives when its member speaks from offscreen — the immediate acknowledgement
    // that lets extra speakers wait in the queue without their line feeling dropped.
    private drawSpeakingRing = (ring: Graphics, role: EmberRosterRole) => {
        ring.clear()
        ring.lineStyle(2, ROLE_COLORS[role], 1)
        ring.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
    }

    // The two halves of a hit's flash, drawn once per card and then only faded: a wash filling the
    // portrait area and a ring around the frame (which outlives the wash, so the card stays marked
    // as the one that was hit).
    private drawWash = (wash: Graphics) => {
        wash.clear()
        wash.beginFill(DAMAGE_WASH, 1)
        wash.drawRoundedRect(
            INNER_MARGIN,
            INNER_MARGIN,
            SLOT_W - INNER_MARGIN * 2,
            SLOT_H - INNER_MARGIN * 2,
            INNER_RADIUS,
        )
        wash.endFill()
    }

    private drawDamageRing = (ring: Graphics) => {
        ring.clear()
        ring.lineStyle(2, DAMAGE_RING, 1)
        ring.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
    }

    // The grey a dead member's card goes under. Left just short of opaque, so the shape of the card
    // -- the portrait, the name -- is still faintly there under the ash rather than replaced by a
    // plain grey oblong.
    private drawPall = (pall: Graphics) => {
        pall.clear()
        pall.beginFill(PALL_COLOR, 1)
        pall.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
        pall.endFill()
    }

    private drawSelection = (selection: Graphics, role: EmberRosterRole) => {
        selection.clear()
        selection.lineStyle(2, ROLE_COLORS[role], 0.95)
        selection.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
    }

    // Add (lord) or remove (non-lord) the animated fire-particle backdrop for a slot, matching its
    // role. The flame sits behind the portrait, clipped to the same inset rounded rect.
    private ensureFlame = (slot: RosterSlot) => {
        const isLord = slot.role === "lord"
        if (isLord && !slot.flame) {
            const mask = this.makeMask()
            const flame = new FlameBackdrop(SLOT_W, SLOT_H)
            flame.mask = mask
            flame.zIndex = Z_FLAME
            slot.container.addChild(mask, flame)
            slot.flame = flame
            slot.flameMask = mask
        } else if (!isLord && slot.flame) {
            slot.flame.mask = null
            slot.container.removeChild(slot.flame, slot.flameMask!)
            slot.flame.destroy()
            slot.flameMask?.destroy()
            slot.flame = undefined
            slot.flameMask = undefined
        }
    }

    // Advance the fire-particle backdrops, the starving pulse, the damage effects and the offscreen
    // speech lane (called each frame by GuiManager).
    animate = (dtMs: number) => {
        this.pulseMs = (this.pulseMs + dtMs) % HUNGER_PULSE_MS
        // 1 -> 0.55 -> 1 over the period. Done with alpha rather than a redraw so a pulsing card
        // costs nothing beyond the value itself.
        const pulse = 0.55 + 0.45 * (0.5 + 0.5 * Math.cos((this.pulseMs / HUNGER_PULSE_MS) * Math.PI * 2))
        // A stronger, faster breath than the hunger bars. Only opacity changes:
        // the pill stays anchored and never shakes or scales.
        this.fireWarningPulseMs = (this.fireWarningPulseMs + dtMs) % FIRE_WARNING_PULSE_MS
        const alarm =
            0.45 + 0.55 * (0.5 + 0.5 * Math.cos((this.fireWarningPulseMs / FIRE_WARNING_PULSE_MS) * Math.PI * 2))
        this.fireWarning.alpha = alarm
        // The chips that need answering breathe on the same clock as the fire alarm beside them, so
        // the lane has one heartbeat rather than several out of step.
        this.statusRow.pulse(alarm)
        this.damageFx.update(dtMs)
        this.slots.forEach((slot, entityId) => {
            slot.flame?.update(dtMs, Number.isFinite(slot.fireStrength) ? slot.fireStrength : 0)
            slot.hungerBar.alpha = slot.hunger !== undefined && slot.hunger <= HUNGER_URGENT ? pulse : 1
            // The same breath the starving bar takes, for the same reason: at this point the clock
            // is the only thing the card is still telling you.
            slot.patienceRing.alpha = slot.patience !== undefined && slot.patience <= PATIENCE_URGENT ? pulse : 1
            this.applyDamage(slot, this.damageFx.frame(entityId))
            this.updateFlare(slot, dtMs)
        })
        // After applyDamage, which parks a card at its resting x -- a card crossing the gap is not
        // at rest, and the join is what owns its position until it lands.
        this.updateJoins(dtMs)
        this.updateDots(dtMs)
        this.updateSpeech(dtMs)
        this.updateDeaths(dtMs)
        this.updateLeaving(dtMs)
    }

    // Burn down a card's arrival flare. Torn down once it is over rather than left drawing nothing,
    // because every card on this strip is paid for per frame on hardware that counts them.
    private updateFlare = (slot: RosterSlot, dtMs: number) => {
        if (slot.flareMs === undefined || !slot.flare) {
            return
        }
        slot.flareMs -= dtMs
        if (slot.flareMs <= 0) {
            slot.container.removeChild(slot.flare)
            slot.flare.destroy()
            slot.flare = undefined
            slot.flareMs = undefined
            return
        }
        drawFlareGlow(slot.flare, {
            remainingMs: slot.flareMs,
            width: SLOT_W,
            height: SLOT_H,
            corner: INNER_RADIUS + 1,
        })
    }

    // A point already on screen, expressed in this widget's own space. Both animations below start
    // from where the card VISUALLY was, and the strip moves underneath them while they run -- it is
    // right-anchored, so admitting or releasing a card shifts every local coordinate in it. Going
    // through the screen keeps the start of a slide pinned to what the player actually saw.
    private localFromScreen = (screenX: number): number => (screenX - this.x) / (this.scale.x || 1)

    private screenFromLocal = (localX: number): number => this.x + localX * this.scale.x

    // Ease in and out, so a card leaves and arrives gently and spends its speed in the middle.
    private ease = (t: number): number => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

    // THE JOIN. A visitor that has been given a job keeps its card -- it is the same person, and
    // the same entity id -- so what the strip shows is that card crossing the gap and taking its
    // place at the end of the household. Nothing else on the strip moves while it happens.
    private updateJoins = (dtMs: number) => {
        let landed = false
        this.slots.forEach(slot => {
            if (slot.joinMs === undefined || slot.joinFromX === undefined) {
                return
            }
            slot.joinMs += dtMs
            const t = Math.min(1, slot.joinMs / JOIN_MS)
            const from = this.localFromScreen(slot.joinFromX)
            slot.container.x = from + (slot.baseX - from) * this.ease(t)
            if (t >= 1) {
                slot.joinMs = undefined
                slot.joinFromX = undefined
                slot.container.x = slot.baseX
                landed = true
            }
        })
        if (landed) {
            this.layout()
            this.guiManager.positionEmberRoster()
        }
    }

    // THE DEPARTURE. Not a death: they gave up on this camp and walked back out, so the card drifts
    // further left -- the way they came -- and fades. The gap is held open until the last of them
    // has gone (see layout), which is what lets the dots run backwards over the space they left.
    private updateLeaving = (dtMs: number) => {
        let finished = false
        this.leaving.forEach((card, entityId) => {
            card.elapsedMs += dtMs
            const t = Math.min(1, card.elapsedMs / LEAVE_MS)
            const from = this.localFromScreen(card.fromX)
            card.slot.container.x = from - LEAVE_DRIFT * this.ease(t)
            card.slot.container.alpha = 1 - t
            if (t >= 1) {
                this.disposeSlot(card.slot)
                this.leaving.delete(entityId)
                finished = true
            }
        })
        if (finished) {
            this.layout()
            this.guiManager.positionEmberRoster()
        }
    }

    // The two dots crawling across the gap: the strip's way of saying that the person on the far
    // side of it is trying to get to this side. They travel TOWARD the household while somebody is
    // asking, and back the other way while one walks out, which is the whole difference between the
    // two events read at a glance.
    private updateDots = (dtMs: number) => {
        if (!this.gapOpen) {
            this.joinDots.visible = false
            return
        }
        const leaving = this.visitorColumns === 0
        // Fed visitors quicken: the bargain is struck and only the job is still to be settled.
        let anyFed = false
        this.slots.forEach(slot => {
            if (slot.role === "visitor" && slot.fed) {
                anyFed = true
            }
        })
        const speed = anyFed ? DOT_FED_SPEEDUP : 1
        this.dotMs = (this.dotMs + dtMs * speed) % DOT_TRAVEL_MS

        const x0 = this.visitorColumns * SLOT_W
        const span = VISITOR_GAP
        const y = SLOT_H / 2
        const color = leaving ? 0x8a8378 : anyFed ? 0xffd98a : 0xffb648

        this.joinDots.visible = true
        this.joinDots.clear()
        for (let i = 0; i < DOT_COUNT; i++) {
            let p = (this.dotMs / DOT_TRAVEL_MS + i * DOT_STAGGER) % 1
            if (leaving) {
                p = 1 - p
            }
            // Faded at both ends so a dot arrives and departs rather than popping into the gap.
            const alpha = Math.sin(Math.PI * ((this.dotMs / DOT_TRAVEL_MS + i * DOT_STAGGER) % 1))
            this.joinDots.beginFill(color, 0.25 + 0.65 * alpha)
            this.joinDots.drawCircle(x0 + span * p, y, DOT_R)
            this.joinDots.endFill()
        }
    }

    // Put one card into the state a hit's frame asks for -- or back to rest when there is no hit.
    // The resting path touches nothing it doesn't have to, so an undamaged strip (the normal case)
    // costs a handful of assignments per card per frame and no redraw at all.
    private applyDamage = (slot: RosterSlot, frame: DamageFrame | null) => {
        if (!frame) {
            if (slot.damageSeq === 0) {
                return
            }
            slot.damageSeq = 0
            slot.container.x = slot.baseX
            slot.container.y = 0
            slot.container.scale.set(1)
            slot.wash.visible = false
            slot.damageRing.visible = false
            if (slot.damageNumber) {
                slot.damageNumber.visible = false
            }
            if (slot.ghostRatio !== undefined) {
                slot.ghostRatio = undefined
                this.drawHpBar(slot.hpBar, slot.hp, slot.hpMax)
            }
            return
        }

        // Scale about the card's centre without a pivot (which `selectedCardAnchor` and the layout
        // below both read `container.x` raw for), by taking back half of what the growth adds.
        const grow = frame.scale - 1
        slot.container.scale.set(frame.scale)
        slot.container.x = slot.baseX + frame.shakeX - (grow * SLOT_W) / 2
        slot.container.y = frame.shakeY - (grow * SLOT_H) / 2

        slot.wash.alpha = frame.flashAlpha
        slot.wash.visible = frame.flashAlpha > 0.01
        slot.damageRing.alpha = frame.ringAlpha
        slot.damageRing.visible = frame.ringAlpha > 0.01

        // The ghost holds still for the first fifth of the effect; only repaint the bar once it has
        // actually moved.
        if (slot.ghostRatio === undefined || Math.abs(slot.ghostRatio - frame.ghostRatio) > 0.004) {
            slot.ghostRatio = frame.ghostRatio
            this.drawHpBar(slot.hpBar, slot.hp, slot.hpMax, frame.ghostRatio)
        }

        const damageNumber = this.ensureDamageNumber(slot)
        if (slot.damageSeq !== frame.seq) {
            slot.damageSeq = frame.seq
            damageNumber.text = `-${frame.amount}`
            damageNumber.x = Math.round(SLOT_W / 2 - damageNumber.width / 2)
        }
        damageNumber.y = DAMAGE_NUMBER_Y - frame.numberRise
        damageNumber.alpha = frame.numberAlpha
        damageNumber.visible = frame.numberAlpha > 0.01
    }

    // The floating "-N" is built on the card's FIRST hit and then reused (its text is re-rasterized
    // per hit, which is once every 500ms at worst) -- a fresh Text per hit would churn a canvas-backed
    // texture through the GPU for every blow landed in the camp.
    private ensureDamageNumber = (slot: RosterSlot): Text => {
        if (!slot.damageNumber) {
            const text = new TextRenderer()
                .role("title")
                .bold()
                .strokeThickness(3)
                .color(DAMAGE_NUMBER_COLOR)
                .render("") as Text
            text.style.stroke = 0x000000
            text.zIndex = Z_DAMAGE_NUMBER
            slot.container.addChild(text)
            slot.damageNumber = text
        }
        return slot.damageNumber
    }

    // Hand the lane this frame's members and whatever they've said out of the player's sight, then
    // draw whichever single line it picked, plus the card flashes it wants.
    private updateSpeech = (dtMs: number) => {
        const members = Array.from(this.slots.entries()).map(([entityId, slot]) => ({
            entityId,
            name: slot.name || "",
        }))
        this.speechLane.update(dtMs, members, this.guiManager.getOffscreenSpeech)

        // Hand the bubble this frame's target; it owns the fade and the swap between speakers.
        const line = this.speechLane.currentLine()
        const slot = line ? this.slots.get(line.entityId) : undefined
        const target: BubbleTarget | null =
            line && slot
                ? {
                      key: `${line.entityId}:${line.text}`,
                      name: line.name,
                      text: line.text,
                      tailX: slot.baseX + SLOT_W / 2,
                      tailY: -PANEL_PAD - FIRE_WARNING_H,
                      clampRight: this.columnX(this.slots.size) + PANEL_PAD,
                      color: ROLE_COLORS[slot.role],
                  }
                : null
        this.speechBubble.update(dtMs, target)

        this.slots.forEach((s, entityId) => {
            const alpha = this.speechLane.pulseAlpha(entityId)
            if (alpha > 0 && s.speakingRole !== s.role) {
                this.drawSpeakingRing(s.speaking, s.role)
                s.speakingRole = s.role
            }
            s.speaking.alpha = alpha
            s.speaking.visible = alpha > 0
        })
    }

    // A visitor has given up on this camp. Hand their card to the drift-out instead of the death
    // sequence: they are alive, they are simply walking back the way they came, and the strip says
    // so by letting the card go the same way -- left, and gone -- while the gap it leaves behind
    // stays open just long enough for the dots to run back across it.
    private beginDeparture = (entityId: EntityId, slot: RosterSlot) => {
        this.slots.delete(entityId)
        this.speechLane.forget(entityId)
        this.damageFx.forget(entityId)
        this.order = this.order.filter(id => id !== entityId)

        // Nothing on a card on its way out is still worth clicking or flashing at the player.
        slot.container.interactive = false
        slot.container.cursor = "default"
        slot.selection.visible = false
        slot.speaking.visible = false
        slot.wash.visible = false
        slot.damageRing.visible = false
        slot.container.scale.set(1)
        if (this.selectedEntityId === entityId) {
            this.selectedEntityId = undefined
            // Whatever their card had put up in the slot above the strip belongs to somebody who is
            // no longer here.
            this.guiManager.closeRosterSelection()
        }

        this.leaving.set(entityId, {
            slot,
            elapsedMs: 0,
            fromX: this.screenFromLocal(slot.container.x),
        })
    }

    // A member has left the roster, which in an ember camp means they died. Hand their card over to
    // the death sequence instead of tearing it down: it holds its column, greys over, and breaks
    // apart (roster_death.ts owns the timings).
    private beginDeath = (entityId: EntityId, slot: RosterSlot) => {
        this.slots.delete(entityId)
        this.speechLane.forget(entityId)
        this.damageFx.forget(entityId)
        this.order = this.order.filter(id => id !== entityId)

        // A dead member is not somebody you can still click through to, and the card's live
        // decoration (the selection ring, a speech flash, a half-finished damage flash) has no
        // business outliving them.
        slot.container.interactive = false
        slot.container.cursor = "default"
        slot.selection.visible = false
        slot.speaking.visible = false
        slot.wash.visible = false
        slot.damageRing.visible = false
        if (slot.damageNumber) {
            slot.damageNumber.visible = false
        }
        slot.container.scale.set(1)
        if (this.selectedEntityId === entityId) {
            this.selectedEntityId = undefined
        }

        const pall = new Graphics()
        this.drawPall(pall)
        pall.alpha = 0
        pall.zIndex = Z_DEATH_PALL
        slot.container.addChild(pall)

        this.dying.set(entityId, {
            slot,
            column: slot.column,
            elapsedMs: 0,
            pall,
            shattered: false,
        })
    }

    // Advance every dying card: drain its colour, break it apart at the halfway mark, fly the shards,
    // and take the whole thing off the strip when it is done.
    private updateDeaths = (dtMs: number) => {
        let finished = false
        this.dying.forEach((card, entityId) => {
            card.elapsedMs += dtMs

            if (!card.shattered) {
                // Over the WHOLE card, bars and name included -- the member is gone, so nothing on
                // the card is still telling the player anything.
                card.pall.alpha = PALL_MAX_ALPHA * greyAt(card.elapsedMs)
            }

            if (!card.shattered && isShattering(card.elapsedMs)) {
                this.shatterCard(card)
            }

            if (card.shattered && card.seeds && card.pieces) {
                const shatterMs = card.elapsedMs - GREY_MS
                card.seeds.forEach((seed, i) => {
                    const piece = card.pieces![i]
                    const motion = fragmentAt(shatterMs, seed)
                    if (!motion) {
                        return
                    }
                    piece.x = seed.x + seed.w / 2 + motion.dx
                    piece.y = seed.y + seed.h / 2 + motion.dy
                    piece.angle = motion.angle
                    piece.alpha = motion.alpha
                })
            }

            if (isFinished(card.elapsedMs)) {
                this.endDeath(entityId, card)
                finished = true
            }
        })
        if (finished) {
            // The gap closes the moment the last shard has gone, rather than on whichever roster tick
            // happens to come next.
            this.layout()
            this.guiManager.positionEmberRoster()
        }
    }

    // Break the (by now grey) card into shards: snapshot it to a texture, slice that into a grid of
    // little sprites, and hide the card itself. Without a renderer -- a test, or a context that is
    // gone -- there is nothing to photograph and nothing being watched either, so the card just goes
    // and the death runs out its clock with the column still held.
    private shatterCard = (card: DyingCard) => {
        card.shattered = true
        const texture = this.captureCard(card.slot)
        if (!texture) {
            card.slot.container.visible = false
            return
        }

        // Clamped to what the photograph ACTUALLY measures, which can be a fraction less than the
        // card it photographed -- see captureCard. `new Texture` throws on a frame that does not fit
        // inside its base, and this runs from the render loop, where a throw used to stop the ticker
        // and freeze the whole client (see the frame guard in index.ts). captureCard now rounds the
        // capture up so this never binds; it stays because the cost of being wrong here is the game.
        const maxW = texture.baseTexture.width
        const maxH = texture.baseTexture.height
        const seeds = seedFragments(SLOT_W, SLOT_H)
            .map(seed => ({
                ...seed,
                w: Math.min(seed.w, maxW - seed.x - FRAME_EPSILON),
                h: Math.min(seed.h, maxH - seed.y - FRAME_EPSILON),
            }))
            // A shard that fell entirely outside the photograph has nothing to show.
            .filter(seed => seed.w > 0 && seed.h > 0)
        const shards = new Container()
        shards.position.set(card.column * SLOT_W, 0)
        const pieces = seeds.map(seed => {
            const piece = new Sprite(new Texture(texture.baseTexture, new Rectangle(seed.x, seed.y, seed.w, seed.h)))
            // Spun about their own middles, so a shard tumbles rather than swinging around a corner.
            piece.anchor.set(0.5)
            piece.x = seed.x + seed.w / 2
            piece.y = seed.y + seed.h / 2
            shards.addChild(piece)
            return piece
        })
        this.addChild(shards)

        card.texture = texture
        card.shards = shards
        card.pieces = pieces
        card.seeds = seeds
        card.slot.container.visible = false
    }

    // Snapshot one card into a texture of its own. The card is drawn at its own origin (its position
    // in the strip is put back straight afterwards) so the shards can be placed by the grid alone.
    private captureCard = (slot: RosterSlot): RenderTexture | null => {
        const renderer = (PixiJsClientRenderer as any)._app?.renderer
        if (!renderer) {
            return null
        }
        try {
            const resolution = renderer.resolution || 1
            // Asked for in whole DEVICE pixels, so the photograph can never come back smaller than
            // the card the shards are cut from -- see captureExtent for why that happens and what it
            // used to cost.
            const texture = RenderTexture.create({
                width: captureExtent(SLOT_W, resolution),
                height: captureExtent(SLOT_H, resolution),
                resolution,
            })
            const savedX = slot.container.x
            const savedY = slot.container.y
            slot.container.position.set(0, 0)
            renderer.render(slot.container, { renderTexture: texture, clear: true })
            slot.container.position.set(savedX, savedY)
            return texture
        } catch (e) {
            // A card that can't be photographed must not take the roster down with it.
            console.warn("[EmberRoster] card snapshot failed", e)
            return null
        }
    }

    private endDeath = (entityId: EntityId, card: DyingCard) => {
        this.dying.delete(entityId)
        card.pall.destroy()
        if (card.shards) {
            this.removeChild(card.shards)
            card.shards.destroy({ children: true })
        }
        // The shards' sub-textures share this base texture, so it goes last, once they are gone.
        card.texture?.destroy(true)
        this.disposeSlot(card.slot)
    }

    private disposeSlot = (slot: RosterSlot) => {
        slot.flare?.destroy()
        slot.speaking.destroy()
        slot.wash.destroy()
        slot.damageRing.destroy()
        disposeTextTextures(slot.container)
        slot.portrait.mask = null
        slot.portrait.remove()
        if (slot.flame) {
            slot.flame.mask = null
            slot.flame.destroy()
            slot.flameMask?.destroy()
        }
        this.removeChild(slot.container)
    }

    // The plate (or plates) behind the cards. A camp with nobody at the fire draws exactly the one
    // rounded rect it always has; a visitor gets a SECOND, separate plate of its own, because the
    // gap has to be a gap between two things rather than a notch cut out of one.
    private drawPanel = (count: number) => {
        this.clear()
        if (count < 1) {
            return
        }
        const h = SLOT_H + PANEL_PAD * 2
        const plate = (x: number, cards: number) => {
            this.beginFill(0x10140f, 0.72)
            this.lineStyle(1, 0xffffff, 0.12)
            this.drawRoundedRect(x - PANEL_PAD, -PANEL_PAD, cards * SLOT_W + PANEL_PAD * 2, h, 8)
            this.endFill()
        }

        const visitors = Math.min(this.visitorColumns, count)
        if (visitors > 0) {
            plate(0, visitors)
        }
        const household = count - visitors
        if (household > 0) {
            plate(this.columnX(visitors), household)
        }
    }

    update = (roster: EmberRosterEntry[]) => {
        // At most ONE hit sound per tick, however many members were hurt in it. The roster speaks
        // twice a second, so a wave mauling the whole camp would otherwise stack three or four
        // sounds into the same frame and arrive as a blur rather than as blows.
        let heardAHit = false

        // Stable order (lord, defenders, foragers) so cards don't shuffle between ticks.
        const ordered = [...roster].sort((a, b) => {
            const o = ROLE_ORDER[a.role] - ROLE_ORDER[b.role]
            return o !== 0 ? o : a.entityId.localeCompare(b.entityId)
        })

        const incoming = new Set(ordered.map(e => e.entityId))
        for (const [entityId, slot] of Array.from(this.slots.entries())) {
            if (!incoming.has(entityId)) {
                // Off the roster means dead (or gone): the card doesn't just disappear, it dies.
                //
                // Except for a VISITOR, who leaves the feed for a second reason -- they ran out of
                // patience and walked back into the dark. They are still alive, and shattering
                // their card would say they were not. The entity itself is asked which it was
                // (hasMemberFallen): a disappointed recruit is still standing in the world at this
                // moment, walking home, while one that was cut down at the fire is not.
                if (slot.role === "visitor" && !this.guiManager.hasMemberFallen(entityId)) {
                    this.beginDeparture(entityId, slot)
                } else {
                    this.beginDeath(entityId, slot)
                }
            }
        }

        ordered.forEach(entry => {
            const appearanceKey = this.appearanceKeyFor(entry)
            let slot = this.slots.get(entry.entityId)

            if (!slot) {
                const container = new Container()
                container.interactive = true
                container.cursor = "pointer"
                container.hitArea = new Rectangle(0, 0, SLOT_W, SLOT_H)
                container.sortableChildren = true

                const chrome = new Graphics()
                this.drawChrome(chrome)
                chrome.zIndex = Z_CHROME
                const mask = this.makeMask()
                const portrait = this.buildPortrait(entry)
                portrait.mask = mask
                portrait.zIndex = Z_PORTRAIT
                const scrim = new Graphics()
                this.drawScrim(scrim)
                scrim.zIndex = Z_SCRIM
                const badge = this.makeBadge(entry.role)
                badge.zIndex = Z_BADGE
                const nameText = this.makeNameText(entry.name)
                nameText.zIndex = Z_NAME
                const hpBar = new Graphics()
                this.drawHpBar(hpBar, entry.hp, entry.hpMax)
                hpBar.zIndex = Z_HP
                const hungerBar = new Graphics()
                this.drawHungerBar(hungerBar, entry.hunger)
                hungerBar.zIndex = Z_HUNGER
                const patienceRing = new Graphics()
                this.drawPatienceRing(patienceRing, entry.patience)
                patienceRing.zIndex = Z_PATIENCE
                // Only a visitor arrives unasked; everyone else on this strip got here because the
                // player hired them, and flaring at a defender walking back into view would train
                // the eye to ignore the one signal that matters.
                const arriving = entry.role === "visitor"
                const flare = arriving ? new Graphics() : undefined
                if (flare) {
                    flare.zIndex = Z_FLARE
                    container.addChild(flare)
                }
                const selection = new Graphics()
                selection.visible = false
                selection.zIndex = Z_SELECTION
                const speaking = new Graphics()
                speaking.visible = false
                speaking.zIndex = Z_SPEAKING
                const wash = new Graphics()
                this.drawWash(wash)
                wash.visible = false
                wash.zIndex = Z_WASH
                const damageRing = new Graphics()
                this.drawDamageRing(damageRing)
                damageRing.visible = false
                damageRing.zIndex = Z_DAMAGE_RING

                container.addChild(
                    chrome,
                    portrait,
                    mask,
                    scrim,
                    badge,
                    nameText,
                    hpBar,
                    hungerBar,
                    patienceRing,
                    selection,
                    speaking,
                    wash,
                    damageRing,
                )
                container.on("pointerdown", () => this.onSlotClick(entry.entityId))

                slot = {
                    container,
                    portrait,
                    mask,
                    badge,
                    nameText,
                    hpBar,
                    hungerBar,
                    hunger: entry.hunger,
                    patienceRing,
                    patience: entry.patience,
                    fed: entry.fed,
                    flare,
                    flareMs: arriving ? FLARE_MS : undefined,
                    selection,
                    appearanceKey,
                    canKick: entry.canKick,
                    role: entry.role,
                    name: entry.name,
                    status: entry.status,
                    hp: entry.hp,
                    hpMax: entry.hpMax,
                    baseX: 0,
                    column: 0,
                    fireStrength: entry.fireStrength ?? NaN,
                    speaking,
                    wash,
                    damageRing,
                    damageSeq: 0,
                }
                this.ensureFlame(slot)
                this.slots.set(entry.entityId, slot)
                this.addChild(container)
                if (arriving) {
                    this.guiManager.playVisitorArrival()
                }
            } else {
                slot.canKick = entry.canKick
                // Rebuild only what actually changed (portrait rebuild + Text textures are costly).
                if (slot.appearanceKey !== appearanceKey) {
                    slot.portrait.mask = null
                    slot.portrait.remove()
                    const portrait = this.buildPortrait(entry)
                    portrait.mask = slot.mask
                    portrait.zIndex = Z_PORTRAIT
                    slot.container.addChild(portrait)
                    slot.portrait = portrait
                    slot.appearanceKey = appearanceKey
                }
                if (slot.role !== entry.role) {
                    // THE HIRE. A stranger has been given a job: same card, same person, different
                    // side of the gap. Remembered in SCREEN coordinates because the strip is about
                    // to be laid out again underneath it (see localFromScreen).
                    if (slot.role === "visitor") {
                        slot.joinFromX = this.screenFromLocal(slot.container.x)
                        slot.joinMs = 0
                    }
                    disposeTextTextures(slot.badge)
                    slot.container.removeChild(slot.badge)
                    slot.badge = this.makeBadge(entry.role)
                    slot.badge.zIndex = Z_BADGE
                    slot.container.addChild(slot.badge)
                    slot.role = entry.role
                }
                if (slot.name !== entry.name) {
                    disposeTextTextures(slot.nameText)
                    slot.container.removeChild(slot.nameText)
                    slot.nameText = this.makeNameText(entry.name)
                    slot.nameText.zIndex = Z_NAME
                    slot.container.addChild(slot.nameText)
                    slot.name = entry.name
                }
                if (slot.hp !== entry.hp || slot.hpMax !== entry.hpMax) {
                    // A member whose health went DOWN since the last tick was hurt -- the roster
                    // carries no hit event of its own, and this is the one place both numbers are
                    // known. Healing and a first sighting fall through to a plain redraw.
                    if (slot.hp !== undefined && entry.hp !== undefined && entry.hpMax) {
                        this.damageFx.hit(entry.entityId, slot.hp, entry.hp, entry.hpMax)
                        if (entry.hp < slot.hp && !heardAHit) {
                            // Somebody in the camp is being hurt out of sight. The card says so; the
                            // sound is what gets the player to look at it. GuiManager keeps quiet for
                            // a member close enough that the world already played their hit.
                            this.guiManager.playOffscreenHit(entry.entityId)
                            heardAHit = true
                        }
                    }
                    slot.hp = entry.hp
                    slot.hpMax = entry.hpMax
                    this.drawHpBar(slot.hpBar, entry.hp, entry.hpMax, slot.ghostRatio)
                }
                if (slot.hunger !== entry.hunger) {
                    this.drawHungerBar(slot.hungerBar, entry.hunger)
                    slot.hunger = entry.hunger
                }
                if (slot.patience !== entry.patience) {
                    this.drawPatienceRing(slot.patienceRing, entry.patience)
                    slot.patience = entry.patience
                }
                slot.fed = entry.fed
                slot.status = entry.status
                slot.fireStrength = entry.fireStrength ?? NaN
                this.ensureFlame(slot)
            }

        })

        this.order = ordered.map(entry => entry.entityId)
        this.layout()
        this.applySelection()
    }

    // Place every card in the strip and redraw the panel behind them. Cards whose member has died
    // keep the column they had, and the living lay out around those gaps -- so a card breaking apart
    // is never slid under by a survivor, and the strip only closes up once it has gone.
    //
    // Called on each roster tick AND the moment a death finishes, because the gap has to close then
    // rather than whenever the next tick happens to arrive.
    private layout = () => {
        const total = this.order.length + this.dying.size

        // How much of the left of the strip belongs to visitors. Counted off the LIVING order only:
        // it is sorted (visitors first, by ROLE_ORDER), so this is simply its leading run -- and a
        // card that is breaking apart has no say in where the gap goes.
        let visitors = 0
        while (visitors < this.order.length && this.slots.get(this.order[visitors])?.role === "visitor") {
            visitors++
        }
        this.visitorColumns = visitors
        // Held open for a join in flight as well. The card crossing the gap has already been
        // re-sorted into the household, so without this the strip would close up around it the
        // instant it was hired and the slide would happen in mid-air, over the top of the camp.
        // Kept open, the space it is leaving empties behind it and the strip's width never moves.
        this.gapOpen = visitors > 0 || this.leaving.size > 0 || this.joining() > 0

        // Dying cards claim their column first, lowest first. An index that no longer exists (the
        // last card of the strip died) falls to the nearest one that does.
        const taken = new Set<number>()
        Array.from(this.dying.values())
            .sort((a, b) => a.column - b.column)
            .forEach(card => {
                let column = Math.min(Math.max(0, card.column), Math.max(0, total - 1))
                while (taken.has(column)) {
                    column = column + 1 >= total ? 0 : column + 1
                }
                taken.add(column)
                card.column = column
                card.slot.column = column
                this.place(card.slot, column)
                card.shards?.position.set(this.columnX(column), 0)
            })

        let column = 0
        this.order.forEach(entityId => {
            const slot = this.slots.get(entityId)
            if (!slot) {
                return
            }
            while (taken.has(column)) {
                column++
            }
            slot.column = column
            this.place(slot, column)
            this.applyDamage(slot, this.damageFx.frame(entityId))
            column++
        })

        this.drawPanel(total)
        this.updateFireWarning()
        this.updateStatusRow()
    }

    // Hand the pill row this frame's cards. Driven off `order` and the RESTING x, so a chip sits over
    // the column its member belongs to rather than following a damage shake -- and so a card that is
    // breaking apart (which keeps its column but has no orders left) carries no chip at all.
    private updateStatusRow = () => {
        // The fire warning's own baseline, so the lord's alarm and everybody else's chip sit on one
        // line, inset from the card by the same air every chip keeps (see the fire warning below).
        this.statusRow.y = -PANEL_PAD - FIRE_WARNING_H
        this.statusRow.sync(
            this.order.map(entityId => {
                const slot = this.slots.get(entityId)
                return {
                    entityId,
                    // The lord's pill is the fire warning; nobody gets two.
                    status: slot && slot.role !== "lord" ? slot.status : undefined,
                    x: slot?.baseX ?? 0,
                }
            }),
        )
    }

    // Use the same 0.3 boundary as the server's cold-health drain. Missing data is
    // not evidence of an extinguished fire (older/initial roster packets may omit it).
    private updateFireWarning = () => {
        const lord = Array.from(this.slots.values()).find(slot => slot.role === "lord")
        const state = !lord || !Number.isFinite(lord.fireStrength) || lord.fireStrength >= 0.3
            ? undefined
            : lord.fireStrength <= 0 ? "No fire" : "Fire low"
        this.fireWarning.visible = state !== undefined
        if (!state || !lord) {
            this.fireWarningState = undefined
            return
        }
        if (state !== this.fireWarningState) {
            this.fireWarningState = state
            this.fireWarningPulseMs = 0
            this.fireWarning.alpha = 1
            const color = state === "No fire" ? 0xff5959 : 0xffbd45
            this.fireWarningTitle.text = state === "No fire" ? "No fire!" : state
            this.fireWarningTitle.tint = 0xfff3e0
            // The same plate every status chip in the row beside it is drawn on, laid out by the same
            // rule: this pill was the first one, and the row generalised it, so the shape now lives in
            // one place (roster_status.ts) and the lord's alarm cannot drift away from the rest of
            // the lane -- including the air that keeps one chip off the next.
            drawPillPlate(this.fireWarning, color, "alarm")
            fitPillText(this.fireWarningTitle)
        }
        this.fireWarning.position.set(lord.baseX + STATUS_PILL_GAP, -PANEL_PAD - FIRE_WARNING_H)
    }

    // The card's resting place. Its ACTUAL position is set by applyDamage, which is what holds the
    // shake -- a roster tick landing mid-jolt would otherwise snap the card back.
    private place = (slot: RosterSlot, column: number) => {
        slot.baseX = this.columnX(column)
        // A card mid-JOIN is being driven across the gap by updateJoins; writing its resting x here
        // would snap it to the far side the instant the roster next ticked, which is most of the
        // way through a 480ms slide. `baseX` is still updated, because that is where it is sliding
        // TO -- and what the connector line and the speech tail should already be aiming at.
        if (slot.joinMs === undefined) {
            slot.container.x = slot.baseX
        }
        slot.container.y = 0
    }

    // Take the whole strip down at once, with no death animation: this is a ROOM CHANGE, not a camp
    // losing its people, and a card cannot break apart over a room the player has already left.
    // (Not `clear`, which is Graphics' own.)
    clearRoster = () => {
        this.dying.forEach((card, entityId) => this.endDeath(entityId, card))
        this.slots.forEach((slot, entityId) => {
            this.disposeSlot(slot)
            this.speechLane.forget(entityId)
            this.damageFx.forget(entityId)
        })
        this.slots.clear()
        this.updateFireWarning()
        this.statusRow.forgetAll()
        this.order = []
        this.selectMember(undefined)
        this.drawPanel(0)
    }

    // A card carries two different offers depending on who is on it. The LORD is the one member the
    // player never gives orders to, so his card opens the follow-cam PiP and lets you watch him.
    // Everyone else is under command, so their card opens THEIR command wheel above the strip --
    // which is the whole point of a roster you can reach an off-screen member through.
    private onSlotClick = (entityId: EntityId) => {
        const slot = this.slots.get(entityId)
        if (!slot) {
            return
        }
        // Whatever the last tap put up comes down first -- both live in the slot above the strip, and
        // only one card is ever the active one. It also makes tapping the ACTIVE card a toggle.
        const wasSelected = this.selectedEntityId === entityId
        this.guiManager.closeRosterSelection()
        if (wasSelected) {
            return
        }
        // Set AFTER the close above: closeRosterSelection clears the selection on its way out.
        this.selectMember(entityId)
        // An inviter can watch or kick their guest. Other player cards and the lord open
        // the follow camera; roster permissions come from the server for this viewer.
        // A VISITOR takes no orders -- they are not crew, and the command wheel has nothing to say
        // to them. What a tap on their card gives you instead is exactly what a tap on the person
        // would: their own verbs ("Give Food", and "Talk" once they are fed), parked in the slot
        // above the strip because the whole reason you are reading their card is that you cannot
        // see them. `openRosterInteractionWheel` carries a "Watch" slice for the follow cam, which
        // is how the one gesture still reaches both.
        if (slot.role === "visitor") {
            this.guiManager.openRosterInteractionWheel(entityId, slot.name)
            return
        }
        if (slot.role === "player" && slot.canKick) {
            this.guiManager.openRosterPlayerWheel(entityId, slot.name)
        } else if (slot.role === "lord" || slot.role === "player") {
            this.guiManager.openFollowCam(entityId, slot.name)
        } else {
            this.guiManager.openRosterCommandWheel(entityId, slot.role)
        }
    }

    // The one place the active card changes. Besides lighting the card itself, the selected member is
    // outlined OUT IN THE WORLD with the same glow a pointed-at entity gets, so it is obvious which
    // character on screen the card belongs to. Everything that selects or deselects goes through here
    // so the world glow can never outlive the card highlight.
    private selectMember = (entityId?: EntityId) => {
        if (this.selectedEntityId === entityId) {
            return
        }
        this.selectedEntityId = entityId
        this.applySelection()
        this.guiManager.highlightEmberMember(entityId)
    }

    // Called by GuiManager when whatever the card opened closes again (the follow-cam, or that
    // member's command wheel), so the highlighted card returns to normal.
    clearSelection = () => {
        this.selectMember(undefined)
    }

    // Called by GuiManager when something it opened HANDS OVER to something else about the same
    // member -- a roster command wheel giving way to the role picker it asked for -- so the card stays
    // lit through the swap rather than going dark for the round trip and coming back.
    holdSelection = (entityId: EntityId) => {
        this.selectMember(entityId)
    }

    // --- What the strip currently holds. Live cards plus the ones still breaking apart, because to
    // everything outside this widget (its width, the gap it is holding) those count as cards too.
    cardCount = (): number => this.slots.size + this.dying.size

    isDying = (entityId: EntityId): boolean => this.dying.has(entityId)

    // Which column a card occupies, living or dying; -1 if it isn't on the strip at all.
    columnOf = (entityId: EntityId): number =>
        this.slots.get(entityId)?.column ?? this.dying.get(entityId)?.column ?? -1

    // A visitor drifting back out into the dark -- alive, unlike a dying card.
    isLeaving = (entityId: EntityId): boolean => this.leaving.has(entityId)

    // Where a card rests in the strip's own space. The gap a visitor opens lives in HERE rather
    // than in the column numbers, so this is the only honest way to ask whether the strip is split.
    cardX = (entityId: EntityId): number => this.slots.get(entityId)?.baseX ?? -1

    // "greying" | "shattering" for a card on its way out, null for anything else.
    deathPhase = (entityId: EntityId): "greying" | "shattering" | null => {
        const card = this.dying.get(entityId)
        return card ? (card.shattered ? "shattering" : "greying") : null
    }

    // Rendered panel extents (in the parent's coordinate space, i.e. including WIDGET_SCALE), so
    // GuiManager can right-/bottom-align the strip as the card count changes. Measured from this
    // widget's origin (x/y) to the panel's right / bottom edge.
    rightExtent = (): number => {
        // Cards still breaking apart hold their column, so they are still part of the strip's width.
        // A card drifting OUT is not: it is already past the left edge of the plate and taking no
        // room, and counting it would drag the whole strip sideways as it went.
        const count = this.slots.size + this.dying.size
        return count < 1 ? 0 : (this.columnX(count) + PANEL_PAD) * WIDGET_SCALE
    }

    bottomExtent = (): number => (SLOT_H + PANEL_PAD) * WIDGET_SCALE

    // Distance from this widget's origin (y) up to the panel's top edge, INCLUDING the reserved
    // speech lane above it. Reserved whether or not a bubble is showing, so whatever sits above the
    // roster (the follow-cam PiP) doesn't jump every time someone speaks.
    topExtent = (): number => (PANEL_PAD + SPEECH_LANE_H) * WIDGET_SCALE

    // Top-centre of one member's card, in this widget's PARENT coordinate space (i.e. including this
    // widget's position and scale), plus the card's role colour. Null when that member has no card.
    // Used to draw a connector line from whatever the card opened back down to the card.
    //
    // `edge` picks which top the point sits on. The follow-cam's line is drawn OVER the strip and
    // stops at the card itself; a roster command wheel's line is drawn UNDER it (so a speech bubble
    // reads over the line) and has to stop at the panel's outer edge, or its end is buried.
    cardAnchor = (
        entityId: EntityId | undefined,
        edge: "card" | "panel" = "card",
    ): { x: number; y: number; color: number } | null => {
        if (!entityId) {
            return null
        }
        const slot = this.slots.get(entityId)
        if (!slot) {
            return null
        }
        // The card's RESTING centre: reading container.x would make the connector line wobble along
        // with a damage shake.
        const localCenterX = slot.baseX + SLOT_W / 2
        return {
            x: this.x + localCenterX * this.scale.x,
            y: this.y - (edge === "panel" ? PANEL_PAD * this.scale.y : 0),
            color: ROLE_COLORS[slot.role],
        }
    }

    // The selected card's anchor, for the follow-cam PiP's connector line.
    selectedCardAnchor = (): { x: number; y: number; color: number } | null =>
        this.cardAnchor(this.selectedEntityId)

    private applySelection = () => {
        this.slots.forEach((slot, entityId) => {
            const selected = this.selectedEntityId === entityId
            if (selected) {
                this.drawSelection(slot.selection, slot.role)
            }
            slot.selection.visible = selected
        })
    }
}
